import { buildProduct } from '@hive/platform-url-schema'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'


import { formatPriceAmount, isAgeRestrictedProduct } from '@entities/Product/helpers'

import ControlCart from '@features/cart/ui/control'
import { FavoriteButton } from '@features/favorite'

import { PRODUCT_NO_PHOTO_PATH } from '@shared/contants/product'
import { MarketingLabel, PriceOffer, Product } from '@shared/generated'
import { useAgeVerification } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import BadgeGroup from '@shared/ui/badge/group'
import Badge from '@shared/ui/badge/item'
import RatingPreview from '@shared/ui/rating/preview/preview'
import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'

import type { MouseEventHandler } from 'react'

export interface ProductPreviewCardProps {
  data: Product;
  className?: string;
}

export default function ProductPreviewCard ({
  data,
  className,
}: ProductPreviewCardProps) {
  const isTablet = useMediaQuery('(max-width: 767px)')
  const navigate = useNavigate()
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()
  const { t: tp } = useTranslation('product')
  const { selectItem } = useAnalytics()
  const productUrl = buildProduct(data.slug as string)

  const handleProductClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }

    e.preventDefault()

    guardAgeRestrictedAction(isAgeControlRequired, () => {
      selectItem(data)
      navigate(productUrl)
    })
  }

  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(data)

  return (
    <div
      className={cn(styles.root, className, {
        [styles.blur]: isAgeControlRequired,
      })}
    >
      <div className={styles.root__content}>
        <Link
          to={productUrl}
          className={styles.root__top}
          onClick={handleProductClick}
        >
          {!!data.preview?.url && (
            <img
              className={styles.root__img}
              src={data.preview?.url || ''}
              alt=""
              loading="lazy"
              fetchPriority="low"
            />
          )}

          {!data.preview?.url && (
            <ReactSVG
              className={styles.root__plug}
              src={PRODUCT_NO_PHOTO_PATH}
              wrapper="span"
            />
          )}

          { data.average_rating && data.average_rating > 0 ? (
            <RatingPreview
              className={styles.root__rating}
              value={data.average_rating || 0}
            />
          ) : null}

          <FavoriteButton
            className={styles.root__favorite}
            id={data.id}
            isFavorite={!!data.isFavorite}
            appearance='icon'
            size='small'
            theme='none'
          />

          <BadgeGroup className={styles.root__badges}>
            {data.marketingLabels &&
              data.marketingLabels.map((item) => {
                return <Badge data={item as MarketingLabel} />
              })}
          </BadgeGroup>

          {isAgeControlRequired && (
            <div className={styles.root__adult_cover}>
              <span className={styles.root__adult_cover_text}>21+</span>
            </div>
          )}
        </Link>
        <div className={styles.root__bottom}>
          <Link
            className={styles['root__bottom-link']}
            to={productUrl}
            onClick={handleProductClick}
          >
            <div className={styles.root__info}>
              {data.priceOffer && (
                <div
                  className={cn(styles.root__price, {
                    [styles._accent]: !!data.priceOffer.oldPrice,
                  })}
                >
                  {formatPrice(data.priceOffer?.price ?? 0)}{' '}

                  {!!data.priceOffer?.oldPrice && (
                    <div className={styles['root__old-price']}>
                      {formatPrice(data.priceOffer.oldPrice)}
                    </div>
                  )}
                </div>
              )}
              <span className={styles.root__priceUnit}>{ tp('product.perItemPrefix')} {formatPriceAmount({ priceOffer: data.priceOffer as PriceOffer, t: tp })}</span>{' '}
            </div>

            <h3 className={styles.root__name}>
              <span className={styles['root__name-inner']}>{data.name}</span>
            </h3>

          </Link>

          <ControlCart
            isTablet={isTablet}
            classNameAddButton={styles.root__cart}
            classNameControl={styles.root__control}
            product={data}
            asBlock
          />
        </div>
      </div>
    </div>
  )
}
